/**
 * Consent Mode bridge: mirrors Shopify's cookie-banner decision into the
 * theme-level Google Tag Manager container (GTM-M8JVKDS7).
 *
 * Why this exists
 * ---------------
 * The GTM container sets a Consent Mode default of "denied" for EU/EEA, UK and
 * Swiss visitors (its consent-initialisation tag). Nothing in the theme ever
 * sent a `consent update`, so visitors in those regions stayed "denied" inside
 * GTM even after accepting Shopify's cookie banner, and every GA4 event fired
 * through GTM from those regions was a cookieless ping. Shopify's own pixels
 * (including the Google & YouTube channel) handle their consent separately;
 * this file only covers the theme-level GTM container.
 *
 * Native primitive: Shopify's Customer Privacy API
 * (`window.Shopify.customerPrivacy`, loaded via `Shopify.loadFeatures`) and
 * the `visitorConsentCollected` document event. No consent policy is encoded
 * here. GTM simply follows whatever the Shopify banner decided, region rules
 * included, because `analyticsProcessingAllowed()` and friends already answer
 * "allowed" wherever consent is not required.
 *
 * Loaded with `defer` from layout/theme.liquid after `content_for_header`.
 * Pinned by tests/consent-mode-bridge.test.js.
 */
(function (window, document) {
  'use strict';

  var lastKey = null;
  var choiceKey = 'mdPrivacyChoice';
  var restoringChoice = false;
  var choiceKeys = ['analytics', 'marketing', 'preferences', 'sale_of_data'];

  // Shopify's native API currently reads consent from navigation Server-Timing.
  // An HTTP-cached Back response can contain the decision from before the banner
  // was answered. Keep only that tab's explicit native choice for this recovery;
  // processingAllowed() is never evidence that the visitor granted consent.
  function explicitChoice(api) {
    if (!api || typeof api.currentVisitorConsent !== 'function') return null;
    try {
      var current = api.currentVisitorConsent();
      var choice = {};
      choiceKeys.forEach(function (key) {
        if (current && current[key] === 'yes') choice[key] = true;
        if (current && current[key] === 'no') choice[key] = false;
      });
      return Object.keys(choice).length ? choice : null;
    } catch (error) { return null; }
  }

  function storedChoice() {
    try {
      var record = JSON.parse(window.sessionStorage.getItem(choiceKey));
      if (!record || record.v !== 1 || !record.choice || typeof record.choice !== 'object') return null;
      var keys = Object.keys(record.choice);
      if (!keys.length || keys.some(function (key) {
        return choiceKeys.indexOf(key) < 0 || typeof record.choice[key] !== 'boolean';
      })) return null;
      return record.choice;
    } catch (error) { return null; }
  }

  function rememberChoice(api) {
    var choice = explicitChoice(api);
    try {
      if (choice) window.sessionStorage.setItem(choiceKey, JSON.stringify({ v: 1, choice: choice }));
      else window.sessionStorage.removeItem(choiceKey);
    } catch (error) { /* Storage is optional; no choice is fabricated. */ }
  }

  function matchesChoice(api, choice) {
    var current = explicitChoice(api);
    return !!current && Object.keys(choice).every(function (key) { return current[key] === choice[key]; });
  }

  function historyReturn(event) {
    if (event && event.persisted) return true;
    try {
      var navigation = window.performance.getEntriesByType('navigation')[0];
      return !!navigation && navigation.type === 'back_forward' && navigation.transferSize === 0;
    } catch (error) { return false; }
  }

  function dismissRecoveredBanner(api, choice) {
    // A partial choice may still need the banner. Never dismiss preferences or
    // anything for a visitor whose current native readback is undecided.
    if (!['analytics', 'marketing', 'preferences'].every(function (key) {
      return typeof choice[key] === 'boolean';
    }) || !matchesChoice(api, choice)) return;
    var banner = document.getElementById('shopify-pc__banner');
    if (banner) {
      banner.style.display = 'none';
      if (banner.contains(document.activeElement) && document.activeElement.blur) document.activeElement.blur();
    }
  }

  function recoverChoice(api, event) {
    if (!historyReturn(event)) { rememberChoice(api); return false; }
    // A current explicit decision always wins, including a later withdrawal.
    // Only an undecided native API can be the stale pre-banner Back response.
    if (explicitChoice(api)) { rememberChoice(api); return false; }
    var choice = storedChoice();
    if (!choice || matchesChoice(api, choice) || typeof api.setTrackingConsent !== 'function') return false;
    if (restoringChoice) return true;
    restoringChoice = true;
    try {
      api.setTrackingConsent(choice, function (error) {
        restoringChoice = false;
        var decision = decisionFromApi(api);
        if (decision) applyConsent(decision);
        if (error || !matchesChoice(api, choice)) return;
        syncFromApi();
        dismissRecoveredBanner(api, choice);
      });
    } catch (error) { restoringChoice = false; return false; }
    return true;
  }

  function gtag() {
    window.dataLayer = window.dataLayer || [];
    // GTM only recognises consent commands pushed as an `arguments` object.
    window.dataLayer.push(arguments);
  }

  function grant(flag) {
    return flag ? 'granted' : 'denied';
  }

  /**
   * Push a Consent Mode update for the given decision. Identical consecutive
   * states are pushed once, so the API read on load and the banner event never
   * double-push. Returns true when a push happened.
   */
  function applyConsent(decision) {
    var analytics = !!(decision && decision.analytics);
    var marketing = !!(decision && decision.marketing);
    var preferences = !!(decision && decision.preferences);
    var key = [analytics, marketing, preferences].join('|');
    if (key === lastKey) return false;
    lastKey = key;
    gtag('consent', 'update', {
      analytics_storage: grant(analytics),
      ad_storage: grant(marketing),
      ad_user_data: grant(marketing),
      ad_personalization: grant(marketing),
      functionality_storage: grant(preferences),
      personalization_storage: grant(preferences)
    });
    return true;
  }

  function allowed(api, method) {
    return typeof api[method] === 'function' ? !!api[method]() : false;
  }

  /** Read the current decision from a Customer Privacy API object. */
  function decisionFromApi(api) {
    if (!api) return null;
    try {
      return {
        analytics: allowed(api, 'analyticsProcessingAllowed'),
        marketing: allowed(api, 'marketingAllowed'),
        preferences: allowed(api, 'preferencesProcessingAllowed')
      };
    } catch (error) {
      return null;
    }
  }

  /** Read the decision carried by a `visitorConsentCollected` event. */
  function decisionFromEvent(event) {
    var detail = event && event.detail;
    if (!detail) return null;
    if (detail.customerPrivacy) return decisionFromApi(detail.customerPrivacy);
    return {
      analytics: !!detail.analyticsAllowed,
      marketing: !!detail.marketingAllowed,
      preferences: !!detail.preferencesAllowed
    };
  }

  function syncFromApi(event) {
    var shopify = window.Shopify;
    var api = shopify && shopify.customerPrivacy;
    if (!api || recoverChoice(api, event)) return;
    var decision = decisionFromApi(api);
    if (decision) applyConsent(decision);
  }

  function init() {
    document.addEventListener('visitorConsentCollected', function (event) {
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (!restoringChoice) rememberChoice(api);
      var decision = decisionFromEvent(event);
      if (decision) applyConsent(decision);
    });
    document.addEventListener('consentTrackingApiLoaded', syncFromApi);
    if (typeof window.addEventListener === 'function') {
      window.addEventListener('pageshow', syncFromApi);
    }

    var shopify = window.Shopify;
    if (!shopify) return;
    if (shopify.customerPrivacy) {
      syncFromApi();
      return;
    }
    if (typeof shopify.loadFeatures === 'function') {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (!error) syncFromApi();
      });
    }
  }

  window.MD_consentBridge = {
    applyConsent: applyConsent,
    decisionFromApi: decisionFromApi,
    decisionFromEvent: decisionFromEvent
  };

  init();
})(window, document);
